import type { JSX } from 'react';
import { useMemo } from 'react';

import { Field, useField, useForm } from 'react-final-form';

import { Radio, RadioGroup } from '@change/ds-forms/next';

import { translationKey } from '@change-corgi/core/i18n';

import { useFormI18n } from 'src/app/shared/form/hooks';
import { requiredValidator } from 'src/app/shared/form/validators';

import { useCategoryLabel } from '../hooks/useCategoryLabel';
import { getCategories } from '../shared/categories';
import type { Category, FormValues, ReportType } from '../shared/types';

type Props = Readonly<{
	type: ReportType;
	countryCode: string;
}>;

const validator = requiredValidator<Category>({
	i18n: { key: translationKey('fe.components.petition_report_abuse.errors.category_required') },
});

function RadioField({ type, category }: { type: ReportType; category: Category }) {
	const getLabel = useCategoryLabel(type, 'category');

	const { input } = useField<FormValues['category']>('category', {
		type: 'radio',
		value: category,
	});

	return (
		<div className="my-1">
			<Radio
				id={`report-abuse-category-${category}`}
				value={input.value ?? ''}
				data-testid={`reportAbuse-category-${category}-radio`}
				data-qa={`reportAbuse-category-${category}-radio`}
			>
				{getLabel(category)}
			</Radio>
		</div>
	);
}

export function CategoryField({ type, countryCode }: Props): JSX.Element {
	const { getErrorMessage } = useFormI18n();
	const form = useForm();

	const categories = useMemo(() => getCategories(type, countryCode), [type, countryCode]);

	return (
		<div className="mb-4">
			<Field<FormValues['category']> name="category" subscription={{ error: true, touched: true }} validate={validator}>
				{({ input: groupInput, meta }) => (
					<RadioGroup
						label=""
						labelIsVisible={false}
						name="category"
						id="report-abuse-category"
						// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
						errorMessage={meta.touched && getErrorMessage(meta.error)}
						isInvalid={meta.touched && !!meta.error}
						onChange={(value: string) => {
							form.change('subCategory', '');
							groupInput.onChange(value);
						}}
					>
						{categories.map((category) => (
							<RadioField type={type} category={category} key={category} />
						))}
					</RadioGroup>
				)}
			</Field>
		</div>
	);
}
